import { useRecoilCallback } from 'recoil';
import {
  steppedHPConfigurations,
  clinicalStepsBaseline,
  hangingProtocolStepIndex,
  hangingProtocolSubStepIndex,
  makeVersionedSteps,
} from '../state';
import { clearAllClinicalSnapshots } from './useClinicalViewportEditing';

/**
 * Clears every piece of stepped-HP state: the steps themselves, the last-saved baseline they are
 * compared against, the step and sub-step indices, and any pending revert snapshots.
 *
 * Deleting a hanging protocol used to leave this to each viewer window's own refetch-and-reinitialize
 * pass. That pass is skipped when a window observes "no HP" while its init guard is still set (the
 * guard exists so a transient null during a refetch does not blank the step chips), so a window that
 * had not yet processed the deletion broadcast kept rendering a step bar for an HP that no longer
 * exists, until the page was refreshed (VX-4738).
 *
 * Calling this from the delete path makes the deletion authoritative instead: the step atoms are
 * BroadcastChannel-synced, so clearing them here clears the other viewer windows too, with no
 * dependence on refetch timing. The version bump from `makeVersionedSteps` is what lets the other
 * windows accept the cleared value.
 */
export function useClearSteppedHPState(): () => void {
  return useRecoilCallback(
    ({ set }) =>
      () => {
        set(steppedHPConfigurations, (prev) => makeVersionedSteps(null, prev, null));
        set(clinicalStepsBaseline, null);
        set(hangingProtocolStepIndex, 0);
        set(hangingProtocolSubStepIndex, 0);
        clearAllClinicalSnapshots();
      },
    []
  );
}
